import React from 'react';
import cn from 'classnames';
import Chip from '@ifit/mp-chip';
import LineWrap from '@ifit/mp-line-wrap';
import ProgressBar from '@ifit/mp-progress-bar';
import Icon from '@ifit/react-icons/dist/components/icon';
import LOCKED_SOLID from '@ifit/react-icons/dist/icons/LOCKED_SOLID';
import LONG_ARROW_RIGHT_LINE from '@ifit/react-icons/dist/icons/LONG_ARROW_RIGHT_LINE';
import STAR_SOLID from '@ifit/react-icons/dist/icons/STAR_SOLID';
import { Text } from './text';
import { DefaultImages } from '@ifit/img/out/defaults';
import { globalizeCompletedDate } from '../../../util/date-utils';
import { getResizedImageUrl } from '../../../util/get-resized-image-url';
import './index.scss';

export interface ICardSeriesProps {
  imageUrl: string;
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
  tagPrimary: string;
  title: string;
  averageRating?: string;
  callToAction?: string;
  callToActionShort?: string;
  className?: string;
  completed?: string;
  leadLine?: string;
  locked?: boolean;
  progress?: number;
  recommended?: boolean;
  subtitle?: string;
  themeClass?: string;
}

export default function CardSeries({
  averageRating,
  callToAction = Text.viewSeries,
  callToActionShort = Text.view,
  className,
  completed,
  imageUrl,
  leadLine,
  locked,
  onClick,
  progress,
  recommended,
  subtitle,
  tagPrimary,
  themeClass,
  title,
  ...rest
}: ICardSeriesProps): JSX.Element {
  const options = {
    width: 344,
    height: 384,
  };
  const resizedImageUrl: string = getResizedImageUrl(imageUrl, DefaultImages.LargeCard, options);

  return (
    <a
      href="#"
      onClick={onClick}
      className={cn('card-series', themeClass, className, {
        locked: Boolean(locked),
      })}
      data-testid="card-series"
      {...rest}
    >
      <article className="card-series-article">
        <div className="card-series-image-container">
          <img src={resizedImageUrl} alt="" className="card-series-image" />
        </div>

        <div className="card-series-details-container">
          <div className="card-series-details">
            <header className="card-series-header">
              {leadLine && (
                <p className="card-series-lead-line" data-testid="card-series-leadline">
                  {leadLine}
                </p>
              )}

              <h2 className="card-series-title">
                <LineWrap text={title} />
              </h2>
              {subtitle && (
                <p className="card-series-subtitle" data-testid="card-series-subtitle">
                  {subtitle}
                </p>
              )}
            </header>

            <div className="card-series-tags">
              <div
                className={cn('card-series-tag-primary', {
                  ['recommended-wrapper']: recommended,
                })}
                data-testid="card-series-tag-primary"
              >
                {completed && (
                  <span data-testid="card-series-completed">
                    <Chip>{globalizeCompletedDate(completed)}</Chip>
                  </span>
                )}
                {tagPrimary && !completed && (
                  <Chip recommended={recommended}>
                    {tagPrimary}
                    {recommended && locked && (
                      <Icon
                        icon={LOCKED_SOLID}
                        size={18}
                        className="card-series-locked-icon"
                        data-testid="card-series-locked-icon"
                      />
                    )}
                  </Chip>
                )}
              </div>

              {locked && !recommended && (
                <div className="locked-chip">
                  <Chip>
                    <Icon
                      icon={LOCKED_SOLID}
                      size={18}
                      className="card-series-locked-icon"
                      data-testid="card-series-locked-icon"
                    />
                  </Chip>
                </div>
              )}
            </div>
          </div>

          {callToAction && (
            <div
              className={cn('card-series-cta', {
                'has-rating': Boolean(averageRating),
              })}
              data-testid="card-series-cta"
            >
              <p className="card-series-cta-text">
                {callToActionShort && (
                  <span className="card-series-text-short" data-testid="card-series-text-short">
                    {callToActionShort}
                  </span>
                )}

                <span className="card-series-text-overflow">{callToAction}</span>

                <Icon icon={LONG_ARROW_RIGHT_LINE} size={24} className="card-series-cta-text-icon" />
              </p>

              {averageRating && (
                <span className="card-series-average-rating" data-testid="card-series-average-rating">
                  <Icon icon={STAR_SOLID} size={16} className="card-series-average-rating-icon" />
                  {averageRating}
                </span>
              )}
            </div>
          )}
          {progress && !completed && <ProgressBar progress={progress * 100} data-testid="card-series-progress" />}
        </div>
      </article>
    </a>
  );
}
